import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { usePermission } from '../hooks/usePermission';

/** One document of a stage, e.g. "INV-001". It is a link only when `to` is set AND the user may open that page. */
export interface FlowRef { text: string; to?: string | null; /** Link even without the stage's keys, e.g. the user's own record. */ own?: boolean }

export interface FlowStage {
    key: string;
    /** Stage name, e.g. "Invoice". */
    label: string;
    /** This stage is the document the user is looking at. */
    current?: boolean;
    /** The stage name itself links here (use when the stage is a single known record). */
    to?: string | null;
    /** Specific records of the stage (a sales order can have several challans). */
    refs?: FlowRef[];
    /** The user needs ANY ONE of these keys to open the linked page; otherwise the stage shows as plain text. */
    permissions?: string[];
    /** The user needs ALL of these keys (e.g. a route guard key plus the API key); otherwise the stage shows as plain text. Combined with `permissions` by AND. */
    allKeys?: string[];
    /** Greyed: the step exists in the business flow but has no screen of its own. */
    muted?: boolean;
    /** Hover text, used to explain a greyed step. */
    hint?: string;
}

const pill = {
    display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, px: 1, py: 0.25, borderRadius: 1,
    border: 1, borderColor: 'divider', fontSize: 13, lineHeight: 1.6, maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere',
} as const;

/** Calls the permission hook once per level, so the hook count never changes: true when ANY of `keys` is held. */
const AnyOf: React.FC<{ keys: string[]; children: (allowed: boolean) => React.ReactNode }> = ({ keys, children }) => {
    const first = usePermission(keys[0]);
    if (first || keys.length <= 1) return <>{children(first)}</>;
    return <AnyOf keys={keys.slice(1)}>{children}</AnyOf>;
};

/** True only when ALL of `keys` are held. One hook call per level, so the hook count never changes. */
const AllOf: React.FC<{ keys: string[]; children: (allowed: boolean) => React.ReactNode }> = ({ keys, children }) => {
    const first = usePermission(keys[0]);
    if (!first) return <>{children(false)}</>;
    if (keys.length <= 1) return <>{children(true)}</>;
    return <AllOf keys={keys.slice(1)}>{children}</AllOf>;
};

/**
 * Where a document sits in its chain. Stages are plain text unless the record really links to a document and
 * the user may open that page. It shows only what the page already holds; it never fetches anything.
 */
const FlowStrip: React.FC<{ stages: FlowStage[]; ariaLabel?: string } & Record<string, unknown>> = ({ stages, ariaLabel, ...rest }) => (
    <Box
        {...rest}
        component="ol"
        aria-label={ariaLabel ?? 'Document flow'}
        sx={{ listStyle: 'none', m: 0, p: 0, mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}
    >
        {stages.map((s, i) => {
            const body = (allowed: boolean) => {
                const link = (to: string | null | undefined, text: string, own = false) => ((allowed || own) && to
                    ? <RouterLink to={to} style={{ color: 'inherit', textDecoration: 'underline' }}>{text}</RouterLink>
                    : <span>{text}</span>);
                const state = s.current ? 'current' : s.muted ? 'muted' : 'normal';
                const linked = (allowed && (!!s.to || (s.refs ?? []).some(r => !!r.to))) || (s.refs ?? []).some(r => r.own && !!r.to);
                return (
                    <Box component="li" key={s.key} data-flow-stage={s.key} data-flow-state={state}
                        aria-current={s.current ? 'step' : undefined} title={s.hint}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}>
                        <Box component="span" sx={{
                            ...pill,
                            ...(s.current ? { bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main', fontWeight: 700 } : {}),
                            ...(s.muted ? { color: 'text.disabled', borderStyle: 'dashed' } : {}),
                            ...(!s.current && !s.muted && linked ? { color: 'primary.main' } : {}),
                        }}>
                            {s.current ? <span>{s.label}</span> : link(s.to, s.label)}
                            {/* the current document's own number is already the page title, so only the label shows */}
                            {s.current ? null : (s.refs ?? []).map((r, j) => (
                                <Typography key={`${r.text}-${j}`} component="span" sx={{ fontSize: 13 }}>
                                    {link(r.to, r.text, r.own)}
                                </Typography>
                            ))}
                        </Box>
                        {i < stages.length - 1 && <Box component="span" aria-hidden sx={{ color: 'text.disabled' }}>{'\u2192'}</Box>}
                    </Box>
                );
            };
            const anyGate = (inner: (allowed: boolean) => React.ReactNode) => (s.permissions && s.permissions.length > 0
                ? <AnyOf keys={s.permissions}>{a => inner(a)}</AnyOf>
                : <>{inner(true)}</>);
            return s.allKeys && s.allKeys.length > 0
                ? <AllOf key={s.key} keys={s.allKeys}>{all => (all ? anyGate(body) : body(false))}</AllOf>
                : <React.Fragment key={s.key}>{anyGate(body)}</React.Fragment>;
        })}
    </Box>
);

export default FlowStrip;
